import { useEffect, useRef } from 'react';
import classes from './SiteCursor.module.scss';


// Курсор-прицел, как на geeksai.io: точка и четыре уголка, которые медленно вращаются.
// Над ссылкой или кнопкой вращение останавливается, а уголки разъезжаются
// и обхватывают элемент по его углам (с лёгким запаздыванием — параллакс) и желтеют.
// Цвет — через mix-blend-mode: difference, поэтому прицел виден на любом фоне.
// Только для мыши (на тач-устройствах не включается). Всё считается в одном
// requestAnimationFrame и пишется прямо в style — без перерисовок React.
const TARGETS = 'a, button, [role="button"], label, summary, [data-cursor="hover"]';
// Здесь нужен системный курсор: ввод текста. В iframe (видео) браузер
// не отдаёт события мыши странице, там остаётся курсор самого iframe.
const NATIVE = 'input, textarea, select, [contenteditable="true"], iframe';
// Огромные кликабельные блоки (карточка целиком) прицелом не обхватываем.
const MAX_TARGET = { width: 560, height: 360 };
const CORNER = 12;
const BORDER = 3;
const SPIN_MS = 2000;
// Уголки в покое — квадрат вокруг точки.
const IDLE = [
  { x: -1.5 * CORNER, y: -1.5 * CORNER },
  { x: 0.5 * CORNER, y: -1.5 * CORNER },
  { x: 0.5 * CORNER, y: 0.5 * CORNER },
  { x: -1.5 * CORNER, y: 0.5 * CORNER },
];

const SiteCursor = () => {
  const wrapRef = useRef(null);
  const cornersRef = useRef([]);

  useEffect(() => {
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    if (!canHover.matches) return undefined;

    const wrap = wrapRef.current;
    const corners = cornersRef.current;
    const root = document.documentElement;
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const pos = { ...mouse };
    const cornerPos = IDLE.map((point) => ({ ...point }));
    let angle = 0;
    let target = null;
    let frame = 0;
    let lastTime = 0;

    root.classList.add('has-site-cursor');

    const render = (now) => {
      const dt = lastTime ? Math.min(64, now - lastTime) : 16;
      lastTime = now;

      const follow = calm ? 1 : 0.45;
      pos.x += (mouse.x - pos.x) * follow;
      pos.y += (mouse.y - pos.y) * follow;

      // Цель могла уехать при прокрутке или исчезнуть из DOM.
      if (target && !target.isConnected) target = null;

      let goals = IDLE;
      if (target) {
        const rect = target.getBoundingClientRect();
        goals = [
          { x: rect.left - BORDER - pos.x, y: rect.top - BORDER - pos.y },
          { x: rect.right + BORDER - CORNER - pos.x, y: rect.top - BORDER - pos.y },
          { x: rect.right + BORDER - CORNER - pos.x, y: rect.bottom + BORDER - CORNER - pos.y },
          { x: rect.left - BORDER - pos.x, y: rect.bottom + BORDER - CORNER - pos.y },
        ];
        // Вращение плавно возвращается к нулю, чтобы уголки встали ровно по углам.
        const back = ((angle % 360) + 360) % 360;
        angle = back > 180 ? back + ((360 - back) * 0.3) : back * 0.7;
      } else if (!calm) {
        angle = (angle + ((dt / SPIN_MS) * 360)) % 360;
      }

      const k = calm ? 1 : (target ? 0.28 : 0.2);
      corners.forEach((corner, i) => {
        cornerPos[ i ].x += (goals[ i ].x - cornerPos[ i ].x) * k;
        cornerPos[ i ].y += (goals[ i ].y - cornerPos[ i ].y) * k;
        corner.style.transform = `translate3d(${cornerPos[ i ].x.toFixed(2)}px, ${cornerPos[ i ].y.toFixed(2)}px, 0)`;
      });
      wrap.style.transform =
        `translate3d(${pos.x.toFixed(2)}px, ${pos.y.toFixed(2)}px, 0) rotate(${angle.toFixed(2)}deg)`;

      frame = requestAnimationFrame(render);
    };
    const start = () => {
      if (!frame) {
        lastTime = 0;
        frame = requestAnimationFrame(render);
      }
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };

    const findTarget = (el) => {
      const found = el?.closest?.(TARGETS);
      if (!found) return null;
      const rect = found.getBoundingClientRect();
      return rect.width > MAX_TARGET.width || rect.height > MAX_TARGET.height ? null : found;
    };

    const setTarget = (next) => {
      if (next === target) return;
      target = next;
      wrap.dataset.target = next ? 'true' : 'false';
    };

    const onMove = (event) => {
      mouse.x = event.clientX;
      mouse.y = event.clientY;
      if (wrap.dataset.visible !== 'true') {
        pos.x = mouse.x;
        pos.y = mouse.y;
        wrap.dataset.visible = 'true';
      }
      const isNative = !!event.target?.closest?.(NATIVE);
      if ((wrap.dataset.native === 'true') !== isNative) wrap.dataset.native = String(isNative);
      setTarget(isNative ? null : findTarget(event.target));
      start();
    };

    // При прокрутке под неподвижной мышью оказывается другой элемент.
    const onScroll = () => {
      if (wrap.dataset.visible !== 'true') return;
      setTarget(findTarget(document.elementFromPoint(mouse.x, mouse.y)));
    };

    const hide = () => {
      wrap.dataset.visible = 'false';
      setTarget(null);
      stop();
    };
    const onLeave = (event) => {
      if (!event.relatedTarget) hide();
    };
    const onDown = () => { wrap.dataset.pressed = 'true'; };
    const onUp = () => { wrap.dataset.pressed = 'false'; };
    const onVisibility = () => {
      if (document.hidden) stop();
      else if (wrap.dataset.visible === 'true') start();
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('pointerout', onLeave);
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('blur', hide);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      root.classList.remove('has-site-cursor');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('pointerout', onLeave);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('blur', hide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <div className={classes.cursor} ref={wrapRef} aria-hidden>
      <span className={classes.dot} />
      {[ 'tl', 'tr', 'br', 'bl' ].map((id, i) => (
        <span
          className={`${classes.corner} ${classes[ id ]}`}
          key={id}
          ref={(node) => { cornersRef.current[ i ] = node; }}
        />
      ))}
    </div>
  );
};

export default SiteCursor;
